// ================================================================
// 問題系統 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：問題瀏覽、搜尋、分類查詢、推薦問題
// ================================================================

import apiClient from './client';

// ================================================================
// 型別定義
// ================================================================

/**
 * 問題類別
 */
export type QuestionCategory = 'SIMPLE' | 'COMMON' | 'MAIN' | 'PAIR' | 'FULL';

/**
 * 問題資料
 */
export interface Question {
  question_id: string;        // 問題ID (如 MAIN001)
  category_id: QuestionCategory;
  question_text: string;      // 問題文字（動態標題）
  function_name: string;      // 對應函數名稱
  price: number;              // 價格 (NTD)
  is_free: boolean | number;  // 是否免費 (0/1 或 true/false)
  requires_matching?: boolean; // 是否需要配對 (可選)
  description?: string;       // 問題描述
  tags?: string;              // 標籤
  query_count?: number;       // 查詢次數
  popularity?: number;        // 熱門度
  category_name?: string;     // 類別名稱

  // 動態時間相關欄位（2026-02-02 新增）
  question_text_original?: string;    // 原始標題
  question_text_template?: string;    // 動態標題模板（含 {{period}} / {{year}} 佔位符）
  time_type?: 'weekly' | 'monthly' | 'yearly' | 'fixed';  // 時間類型
  time_range_start?: string;   // 時間範圍起始日期（YYYY-MM-DD）
  time_range_end?: string;     // 時間範圍結束日期（YYYY-MM-DD）
  period_label?: string;       // 時間標籤（如「本週」「下週」）
  is_next_period?: boolean;    // 是否為下一個時間段
}

/**
 * 問題類別資訊
 */
export interface Category {
  category_id: QuestionCategory;
  category_name: string;      // 類別名稱
  price: number;              // 類別價格
  description?: string;       // 類別描述
  question_count?: number;    // 問題數量
}

/**
 * 問題列表響應
 */
export interface QuestionsResponse {
  success: boolean;
  count: number;
  questions: Question[];
  error?: string;
  message?: string;
}

/**
 * 問題詳情響應
 */
export interface QuestionDetailResponse {
  success: boolean;
  question: Question;
  error?: string;
  message?: string;
}

/**
 * 類別列表響應
 */
export interface CategoriesResponse {
  success: boolean;
  count: number;
  categories: Category[];
  error?: string;
  message?: string;
}

/**
 * 推薦問題響應
 */
export interface RecommendationsResponse {
  success: boolean;
  count: number;
  recommendations: Question[];
  error?: string;
}

/**
 * 搜尋響應
 */
export interface SearchResponse {
  success: boolean;
  count: number;
  keyword: string;
  questions: Question[];
  error?: string;
}

// ================================================================
// API 函數
// ================================================================

/**
 * 取得所有問題類別
 *
 * @returns 類別列表
 *
 * @example
 * const categories = await getCategories();
 * console.log(categories.categories);
 */
export const getCategories = async (): Promise<CategoriesResponse> => {
  try {
    const response = await apiClient.get<CategoriesResponse>('/api/questions/categories');
    return response as any as CategoriesResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢類別失敗');
  }
};

/**
 * 取得問題列表
 *
 * @param category_id - 類別ID（選填）
 * @param is_free - 是否僅顯示免費問題（選填）
 * @returns 問題列表
 *
 * @example
 * // 取得所有問題
 * const all = await getQuestions();
 *
 * // 取得特定類別問題
 * const main = await getQuestions('MAIN');
 *
 * // 取得免費問題
 * const free = await getQuestions(undefined, true);
 */
export const getQuestions = async (
  category_id?: QuestionCategory,
  is_free?: boolean
): Promise<QuestionsResponse> => {
  try {
    const params: any = {};

    if (category_id) {
      params.category_id = category_id;
    }

    if (is_free !== undefined) {
      params.is_free = is_free ? 'true' : 'false';
    }

    const response = await apiClient.get<QuestionsResponse>('/api/questions', { params });
    return response as any as QuestionsResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢問題失敗');
  }
};

/**
 * 取得熱門問題列表
 *
 * @param limit - 限制數量 (預設 10)
 * @returns 熱門問題列表
 *
 * @example
 * const popular = await getPopularQuestions(20);
 */
export const getPopularQuestions = async (limit: number = 10): Promise<QuestionsResponse> => {
  try {
    const response = await apiClient.get<QuestionsResponse>('/api/questions/popular', {
      params: { limit }
    });
    return response as any as QuestionsResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢熱門問題失敗');
  }
};

/**
 * 關鍵字搜尋問題
 *
 * @param keyword - 搜尋關鍵字
 * @returns 搜尋結果
 *
 * @example
 * const results = await searchQuestions('事業');
 */
export const searchQuestions = async (keyword: string): Promise<SearchResponse> => {
  try {
    if (!keyword || keyword.trim() === '') {
      throw new Error('請輸入搜尋關鍵字');
    }

    const response = await apiClient.get<SearchResponse>('/api/questions/search', {
      params: { keyword: keyword.trim() }
    });
    return response as any as SearchResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '搜尋問題失敗');
  }
};

/**
 * 取得問題詳情
 *
 * @param question_id - 問題ID
 * @returns 問題詳情
 *
 * @example
 * const detail = await getQuestionDetail('MAIN001');
 */
export const getQuestionDetail = async (question_id: string): Promise<QuestionDetailResponse> => {
  try {
    const response = await apiClient.get<QuestionDetailResponse>(`/api/questions/${question_id}`);
    return response as any as QuestionDetailResponse;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('問題不存在');
    }
    throw new Error(error.response?.data?.error || '查詢問題詳情失敗');
  }
};

/**
 * 取得相關推薦問題
 *
 * @param question_id - 問題ID
 * @returns 推薦問題列表
 *
 * @example
 * const recommendations = await getRecommendedQuestions('MAIN001');
 */
export const getRecommendedQuestions = async (question_id: string): Promise<RecommendationsResponse> => {
  try {
    const response = await apiClient.get<RecommendationsResponse>(
      `/api/questions/${question_id}/recommendations`
    );
    return response as any as RecommendationsResponse;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || '查詢推薦問題失敗');
  }
};

// ================================================================
// 輔助函數
// ================================================================

/**
 * 取得類別名稱
 */
export const getCategoryName = (category_id: QuestionCategory): string => {
  const names: Record<QuestionCategory, string> = {
    'SIMPLE': '優惠咨詢',
    'COMMON': '常見問題',
    'MAIN': '自我分析',
    'PAIR': '配對分析',
    'FULL': '完整分析'
  };
  return names[category_id] || category_id;
};

/**
 * 取得類別價格
 */
export const getCategoryPrice = (category_id: QuestionCategory): number => {
  const prices: Record<QuestionCategory, number> = {
    'SIMPLE': 10,
    'COMMON': 49,
    'MAIN': 99,
    'PAIR': 199,
    'FULL': 399
  };
  return prices[category_id] || 0;
};

/**
 * 取得類別描述
 */
export const getCategoryDescription = (category_id: QuestionCategory): string => {
  const descriptions: Record<QuestionCategory, string> = {
    'SIMPLE': '優惠咨詢問題，快速簡易分析',
    'COMMON': '日常生活相關問題，快速查詢',
    'MAIN': '自我分析問題，涵蓋事業、感情、健康等主題',
    'PAIR': '雙人配對分析，了解彼此契合度',
    'FULL': '完整流年流月運勢分析報告'
  };
  return descriptions[category_id] || '';
};

/**
 * 取得類別顏色（用於 UI）
 */
export const getCategoryColor = (category_id: QuestionCategory): string => {
  const colors: Record<QuestionCategory, string> = {
    'SIMPLE': '#4caf50',   // 綠色
    'COMMON': '#2196f3',   // 藍色
    'MAIN': '#ff9800',     // 橙色
    'PAIR': '#e91e63',     // 粉紅色
    'FULL': '#9c27b0'      // 紫色
  };
  return colors[category_id] || '#9e9e9e';
};

/**
 * 驗證問題 ID 格式
 */
export const validateQuestionId = (question_id: string): boolean => {
  return /^(FULL|MAIN|PAIR|COMMON|SIMPLE)\d{3}$/.test(question_id);
};

/**
 * 根據當前日期計算動態時間標籤
 * - monthly: 1~15 日 → 本月；16 日後 → 下個月
 * - yearly:  1~8 月 → 今年；9 月以後 → 明年
 * - weekly:  週一~三 → 本週；週四~日 → 下週
 */
export const getDynamicPeriodLabel = (
  timeType: Question['time_type'],
  date: Date = new Date()
): { label: string; isNext: boolean } => {
  const day = date.getDate();
  const month = date.getMonth() + 1; // 1-12
  const weekday = date.getDay();     // 0=Sun, 1=Mon ... 6=Sat

  if (timeType === 'monthly') {
    const isNext = day > 15;
    return { label: isNext ? '下個月' : '本月', isNext };
  }
  if (timeType === 'yearly') {
    const isNext = month > 8;
    return { label: isNext ? '明年' : '今年', isNext };
  }
  if (timeType === 'weekly') {
    // 週四(4)、週五(5)、週六(6)、週日(0) → 下週
    const isNext = weekday === 0 || weekday >= 4;
    return { label: isNext ? '下週' : '本週', isNext };
  }
  return { label: '', isNext: false };
};

/**
 * 動態替換問題描述中的時間詞語
 * 依據 time_type 和當前日期，將描述文字中的時間詞語替換為正確的標籤
 */
export const getDynamicDescription = (
  question: Question,
  date: Date = new Date()
): string => {
  const { time_type, description } = question;
  if (!description) return '';
  if (!time_type || time_type === 'fixed') return description;

  const { label } = getDynamicPeriodLabel(time_type, date);
  if (!label) return description;

  if (time_type === 'monthly') {
    return description
      .replace(/下個月/g, label)
      .replace(/未來一個月/g, label);
  }
  if (time_type === 'yearly') {
    return description
      .replace(/今年/g, label)
      .replace(/明年/g, label);
  }
  if (time_type === 'weekly') {
    return description
      .replace(/未來七天/g, label)
      .replace(/下週/g, label)
      .replace(/本週/g, label);
  }
  return description;
};

/**
 * 動態替換問題標題中的時間詞語
 * 使用 question_text_template（若有）或直接替換 question_text 中的關鍵字
 */
export const getDynamicQuestionTitle = (
  question: Question,
  date: Date = new Date()
): string => {
  const { time_type, question_text, question_text_template } = question;

  if (!time_type || time_type === 'fixed') return question_text;

  const { label } = getDynamicPeriodLabel(time_type, date);
  if (!label) return question_text;

  // 優先用 template（已由後端填好 {{period}} / {{year}} 佔位符）
  if (question_text_template) {
    return question_text_template
      .replace(/\{\{period\}\}/g, label)
      .replace(/\{\{year\}\}/g, label);
  }

  // fallback：直接替換 question_text 中的時間關鍵字
  if (time_type === 'monthly') {
    return question_text
      .replace(/下個月/g, label)
      .replace(/未來一個月/g, label);
  }
  if (time_type === 'yearly') {
    return question_text
      .replace(/今年/g, label)
      .replace(/明年/g, label);
  }
  if (time_type === 'weekly') {
    return question_text
      .replace(/未來七天/g, label)
      .replace(/下週/g, label)
      .replace(/本週/g, label);
  }
  return question_text;
};
